import { useEffect, useRef, useState } from 'react';

/**
 * Revelado al entrar en pantalla: sutil y una sola vez.
 *
 * La animación acá tiene un trabajo concreto —guiar la lectura hacia abajo—,
 * no llamar la atención sobre sí misma: 14px de desplazamiento y medio segundo.
 * Se dispara con IntersectionObserver (nada de escuchar el scroll) y se
 * desconecta al revelarse, así una landing larga no queda observando decenas de
 * elementos.
 *
 * Con `prefers-reduced-motion` no anima: aparece y ya.
 */
export function Revelar({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  /** Escalonado en ms, para que un grupo entre en cascada y no de golpe. */
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const nodo = ref.current;
    if (!nodo) return;
    if (
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        }
      },
      // Se revela un poco ANTES de entrar del todo: si aparece justo en el
      // borde, se percibe como un salto en vez de una entrada.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );
    obs.observe(nodo);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(14px)',
        transition: `opacity 620ms cubic-bezier(.22,.7,.3,1) ${delay}ms, transform 620ms cubic-bezier(.22,.7,.3,1) ${delay}ms`,
        willChange: visible ? 'auto' : 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}
